import React, { useEffect, useState } from "react";
import { Clock, AlertTriangle } from "lucide-react";

/**
 * KycSlaCountdown
 * ---------------
 * Live-updating pill showing minutes-to-breach for a KYC application.
 *
 * Props:
 *   targetTime   — ISO string of the moment SLA breaches (submitted_at + sla_hours)
 *   slaHours     — total SLA in hours (used to compute % remaining for color tier)
 *   status       — current KYC status (only "Under Review" gets the live pill; other
 *                  statuses show a static "resolved" chip)
 *   testId       — optional data-testid override
 *
 * Colour tiers:
 *   > 25% remaining      → emerald   (comfortable)
 *   5% – 25% remaining   → amber     (approaching breach)
 *   < 5% remaining       → rose      (imminent)
 *   Past target          → rose + pulse (breached)
 */
export default function KycSlaCountdown({ targetTime, slaHours, status, testId }) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!targetTime) return undefined;
    // Tick every 30s — fast enough to feel live, slow enough to not thrash renders.
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, [targetTime]);

  if (!targetTime) return null;

  const target = new Date(targetTime).getTime();
  if (Number.isNaN(target)) return null;

  const totalMs = (slaHours || 48) * 60 * 60 * 1000;
  const remainingMs = target - now;
  const pctRemaining = Math.max(-100, Math.min(100, (remainingMs / totalMs) * 100));
  const breached = remainingMs <= 0;

  // Non-Under-Review states shouldn't tick — they've either been resolved or aren't in SLA.
  if (status && status !== "Under Review") {
    return null;
  }

  let tone = "emerald";
  if (breached) tone = "rose-pulse";
  else if (pctRemaining < 5) tone = "rose";
  else if (pctRemaining < 25) tone = "amber";

  const label = breached ? formatBreached(-remainingMs) : formatRemaining(remainingMs);

  const toneClass = {
    emerald: "bg-emerald-50 border-emerald-300 text-emerald-700",
    amber: "bg-amber-50 border-amber-300 text-amber-800",
    rose: "bg-rose-50 border-rose-300 text-rose-800",
    "rose-pulse": "bg-rose-100 border-rose-400 text-rose-900 animate-pulse",
  }[tone];

  const Icon = breached ? AlertTriangle : Clock;

  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border font-heading uppercase tracking-widest text-[10px] ${toneClass}`}
      title={breached ? `SLA breached ${label} ago` : `${label} until SLA breach`}
      data-testid={testId || "kyc-sla-countdown"}
      data-tone={tone}
    >
      <Icon size={11} />
      {breached ? `SLA · ${label}` : label}
    </span>
  );
}

function formatRemaining(ms) {
  if (ms <= 0) return "0m";
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h >= 24) {
    const d = Math.floor(h / 24);
    const rh = h % 24;
    return rh > 0 ? `${d}d ${rh}h` : `${d}d`;
  }
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatBreached(ms) {
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h >= 24) {
    const d = Math.floor(h / 24);
    const rh = h % 24;
    return rh > 0 ? `${d}d ${rh}h over` : `${d}d over`;
  }
  if (h > 0) return `${h}h ${m}m over`;
  return `${m}m over`;
}
